import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { STATIC_FRONTEND_MODE } from "@/lib/backend";

const CmsContext = createContext(null);

export const DEFAULTS = {
  hero_badge_text: "DSITESOLUTION",
  hero_title: "기획·개발·운영까지\n성장을 설계하는 디지털 파트너",
  hero_subtitle:
    "매출은 높이고, 운영은 스마트하게.\n\n디지털 기술로 맞춤형 웹 플랫폼, AI 솔루션, 업무 자동화를 연결하여\n고객의 매출 성장과 운영 효율을 극대화하는 비즈니스를 설계합니다.",
  cta_primary_text: "무료 상담 신청",
  cta_primary_link: "#contact",
  cta_secondary_text: "서비스 살펴보기",
  cta_secondary_link: "/about#services",
  about_title: "문제를 디자인하고,\n가능성을 개발합니다.",
  about_body:
    "D Site Solution은 웹사이트·플랫폼·AI·자동화를 아우르는\n통합 디지털 솔루션 기업입니다.\n\n단순한 홈페이지 제작이 아닌, 분석 → 전략 → 실행의 체계적 과정으로\n비즈니스 성장에 도움이 되는 디지털 환경을 구축합니다.",
  services_title: "웹·플랫폼·AI를 하나로",
  services_body: "",
  portfolio_title: "실제 비즈니스에 결과를 만들어낸 프로젝트.",
  portfolio_body: "",
  process_title: "명확한 단계, 투명한 협업 프로세스.",
  process_body: "",
  ai_platform_title: "AI 코드 리뷰 SaaS 플랫폼",
  ai_platform_body: "",
  media_logo: "/brand/logo-icon.png?v=2",
  media_footer_wordmark: "/brand/logo-wordmark-text.png",
  media_favicon: "/favicon-32x32.png",
  company_name: "DSiteSolution",
  company_email: "dsitesolution@dsitesolution.com",
  footer_copyright: "© DSiteSolution. All rights reserved.",
  kakao_channel_url: "https://pf.kakao.com/_rmpFn/chat",
  site_title: "DSiteSolution",
  seo_description:
    "D Site Solution — 웹사이트·플랫폼 개발, AI·자동화, 브랜딩·마케팅 통합 디지털 솔루션 기업.",
  seo_keywords: "대한민국 웹개발, 홈페이지 제작, AI 챗봇, 업무 자동화",
  seo_og_title: "DSITESOLUTION | AI · WEB · AUTOMATION",
  seo_og_description: "기업 홈페이지 제작, 웹프로그램 개발, AI 솔루션 전문 기업",
  seo_og_url: "",
  seo_canonical_url: "https://dsitesolution.com",
  seo_robots_enabled: "true",
  seo_sitemap_enabled: "true",
  seo_google_verification: "",
  seo_naver_verification: "",
  seo_ga_id: "",
  seo_ga_enabled: "false",
  seo_gtm_id: "",
  seo_gtm_enabled: "false",
  seo_meta_pixel_id: "",
  seo_meta_pixel_enabled: "false",
  media_og_image: "",
  main_hero_en_line1: "Beyond Websites.",
  main_hero_en_line2_before: "We Build ",
  main_hero_en_line2_highlight: "Growth Systems.",
  main_hero_kr_line1: "웹사이트 그 이상.",
  main_hero_kr_line2_highlight: "성장 시스템",
  main_hero_kr_line2_after: "을 만듭니다.",
  main_hero_en_tagline: "WEB, AI, AUTOMATION, SAAS",
  main_hero_kr_tagline: "웹, AI, 자동화, SaaS",
  main_hero_en_cta_lead: "Explore our work in one place",
  main_hero_en_cta_label: "View Portfolio",
  main_hero_kr_cta_lead: "제작 사례를 한곳에서 확인하세요",
  main_hero_kr_cta_label: "포트폴리오 바로가기",
  main_hero_cta_url: "/portfolio/brand-web",
  media_main_hero_video: "",
  company_ceo: "한나래",
  contact_eyebrow: "Contact",
  contact_title: "지금 바로\n상담을 시작하세요.",
  contact_description:
    "비즈니스 정보가 정리되어 있지 않아도 괜찮습니다. 첫 미팅에서 함께 진단합니다. 평균 24시간 내 답변드립니다.",
  automation_eyebrow: "Automation Workflow",
  automation_title: "상담에서 운영까지\n한 흐름으로 자동화합니다.",
  automation_description:
    "사람이 반복해야 했던 단계를 AI와 자동화가 대신합니다. 비즈니스 흐름에 맞춰 필요한 곳에만 자동화를 설계해 운영 비용을 절감합니다.",
  brand_name_en: "DSiteSolution",
  brand_alternate_names: "디사이트솔루션, DSITESOLUTION",
  brand_summary: "웹·AI·자동화로 비즈니스 성장을 설계하는 디지털 파트너.",
  brand_expertise: "기업 홈페이지 제작, 웹 플랫폼 개발, AI 솔루션, 업무 자동화",
  brand_target_industries: "기업, 교육, SaaS, 커머스",
  brand_service_region: "대한민국",
  seo_bing_verification: "",
};

const CACHE_KEY = "dsite_cms_cache_v14";
const AUTH_LIGHTWEIGHT_ROUTES = new Set(["/signup", "/login", "/admin/login"]);
const LEGACY_CACHE_KEYS = [
  "dsite_cms_cache",
  "dsite_cms_cache_v3",
  "dsite_cms_cache_v4",
  "dsite_cms_cache_v5",
  "dsite_cms_cache_v7",
  "dsite_cms_cache_v8",
  "dsite_cms_cache_v9",
  "dsite_cms_cache_v10",
  "dsite_cms_cache_v11",
  "dsite_cms_cache_v12",
  "dsite_cms_cache_v13",
];

const STALE_HERO_SUBTITLE_MARKERS = [
  "단순 홈페이지 제작",
  "단순한 제작을 넘어",
  "고객사의 매출",
  "최적의 디지털 솔루션을 제공",
  "맞춤형 웹 플랫폼, AI 솔루션, 업무 자동화를 결합",
];

function isStaleHeroSubtitle(value) {
  if (!value || typeof value !== "string") return false;
  return STALE_HERO_SUBTITLE_MARKERS.some((m) => value.includes(m));
}

function isStaleHeroTitle(value) {
  if (!value || typeof value !== "string") return false;
  const text = value.trim();
  return (
    (text.startsWith("디지털 기술로") && !text.includes("기획·개발")) ||
    (text.includes("비즈니스 성장") && !text.includes("디지털 파트너"))
  );
}

function isBackendMediaPath(value) {
  if (!value || typeof value !== "string") return false;
  return value.startsWith("/api/") || value.includes("/api/uploads/") || value.includes("/api/media/");
}

function sanitizeMediaField(value, fallback) {
  if (!value || isBackendMediaPath(value)) return fallback;
  return value;
}

/** API/캐시에 남은 구버전 히어로 문구를 코드 기본값으로 덮어씁니다. */
export function mergeCms(apiData) {
  const merged = { ...DEFAULTS, ...(apiData || {}) };
  merged.media_logo = sanitizeMediaField(merged.media_logo, DEFAULTS.media_logo);
  merged.media_footer_wordmark = sanitizeMediaField(
    merged.media_footer_wordmark,
    DEFAULTS.media_footer_wordmark,
  );
  merged.media_favicon = sanitizeMediaField(merged.media_favicon, DEFAULTS.media_favicon);
  merged.media_og_image = sanitizeMediaField(merged.media_og_image, DEFAULTS.media_og_image);
  if (
    !merged.media_logo ||
    merged.media_logo === "" ||
    merged.media_logo.includes("logo-transparent") ||
    merged.media_logo.includes("logo-white.png") ||
    merged.media_logo.endsWith("logo-wordmark.png") ||
    merged.media_logo.includes("brand-identity-board") ||
    merged.media_logo.includes("brand-intro-symbol")
  ) {
    merged.media_logo = DEFAULTS.media_logo;
  }
  if (
    !merged.media_footer_wordmark ||
    merged.media_footer_wordmark === "" ||
    merged.media_footer_wordmark.includes("logo-wordmark.png")
  ) {
    merged.media_footer_wordmark = DEFAULTS.media_footer_wordmark;
  }
  if (
    isStaleHeroSubtitle(merged.hero_subtitle) ||
    (merged.hero_subtitle?.includes("연결하여 고객의") &&
      !merged.hero_subtitle?.includes("연결하여\n고객의"))
  ) {
    merged.hero_subtitle = DEFAULTS.hero_subtitle;
  }
  if (isStaleHeroTitle(merged.hero_title)) {
    merged.hero_title = DEFAULTS.hero_title;
  }
  if (
    merged.hero_badge_text === "AI 기반 개발 생산성 플랫폼" ||
    merged.hero_badge_text === "AI • WEB • AUTOMATION"
  ) {
    merged.hero_badge_text = DEFAULTS.hero_badge_text;
  }
  return merged;
}

function purgeLegacyCaches() {
  try {
    for (const key of LEGACY_CACHE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    /* private mode */
  }
}

purgeLegacyCaches();

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return mergeCms(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function CmsProvider({ children }) {
  // Use last-known CMS (localStorage) as the initial state to avoid
  // flashing the fallback hero background on hard refresh.
  const cached = readCache();
  const [cms, setCms] = useState(cached || { ...DEFAULTS });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (STATIC_FRONTEND_MODE) {
      setCms(mergeCms(readCache() || DEFAULTS));
      setLoading(false);
      return;
    }

    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (AUTH_LIGHTWEIGHT_ROUTES.has(path)) {
      setCms(mergeCms(readCache() || DEFAULTS));
      setLoading(false);
      return;
    }

    try {
      const res = await api.get("/cms", { timeout: 8000 });
      const merged = mergeCms(res.data);
      setCms(merged);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
      } catch {
        /* quota / private mode */
      }
    } catch {
      // keep cached/defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <CmsContext.Provider value={{ cms, loading, refresh }}>
      {children}
    </CmsContext.Provider>
  );
}

export function useCms() {
  const ctx = useContext(CmsContext);
  if (!ctx) return { cms: DEFAULTS, loading: false, refresh: () => {} };
  return ctx;
}

/** Draft overlay for CMS live preview. Does not write cache or DB. */
export function CmsOverrideProvider({ value, children }) {
  const parent = useContext(CmsContext);
  const cms = mergeCms({ ...(parent?.cms || DEFAULTS), ...(value || {}) });
  return (
    <CmsContext.Provider value={{ cms, loading: false, refresh: parent?.refresh || (() => {}) }}>
      {children}
    </CmsContext.Provider>
  );
}

export function getKakaoChannelUrl(cms) {
  return cms?.kakao_channel_url || DEFAULTS.kakao_channel_url;
}
